import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faTicketAlt,
  faCrown,
  faDoorOpen,
  faCalendarAlt,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

const filmTourImage = 'https://BelvoirCastle.b-cdn.net/Tours/film-lovers.webp';

// Import images

const castleImage = 'https://BelvoirCastle.b-cdn.net/Castle/castle2.webp';
const butlerTourImage = 'https://BelvoirCastle.b-cdn.net/Castle/spiral.webp';

const ToursContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const ToursGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TourCardWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const TourCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const DateList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const DateItem = styled.li`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  padding-left: 0.25rem;
`;

const MoreDatesBadge = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  font-style: italic;
  color: ${props => props.theme.colors.accent};
  margin-top: 0.25rem;
`;

const TourImage = styled.div`
  height: 250px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: ${props => props.image.includes('butler-saw') ? 'center 25%' : 'center'};
`;

const TourContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const TourTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const TourDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const TourDateTime = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  margin-bottom: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
    margin-top: 0.2rem;
    flex-shrink: 0;
  }
`;

const TourDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 1.25rem;
`;

const BookNowLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 0.85rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.25s ease;
  border: 2px solid ${props => props.theme.colors.accent};

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
  }
`;

const FeaturedTours = () => {
  const tours = [
    {
      title: "The Crown Tour",
      description: "Join our knowledgeable guides and step into the State Rooms that were used as locations in the popular Netflix Series, The Crown and films such as The Young Victoria and Victoria & Abdul. Hear firsthand about the film crews using our beautiful Castle and Grounds in location filming.",
      duration: "1 ½ hours",
      time: "2:00pm",
      price: "£5 per person",
      icon: faCrown,
      image: filmTourImage,
      dates: [
        "Thursday 2nd April",
        "Thursday 9th April",
        "Thursday 16th April",
        "Thursday 23rd April",
        "Thursday 7th May",
        "Thursday 28th May",
      ],
      moreDatesTBC: true,
      bookingUrl: "/featured-tours/film-lovers",
      bookingPath: "/booking-journey?locationId=978&pageId=de28c2dc-c033-4de1-a18b-615b3c224cfa"
    },
    {
      title: "What the Butler Saw",
      description: "Join one of our knowledgeable guides on tour taking you to areas of the Castle used by household staff in times gone by. Hear fascinating stories of how the house was run downstairs and, on the estate, including innovations that were used at Belvoir Castle.",
      duration: "1 ½ hours",
      time: "2:00pm",
      price: "£5 per person",
      icon: faDoorOpen,
      image: butlerTourImage,
      dates: [
        "Monday 7th September",
        "Friday 11th September",
        "Monday 14th September",
        "Friday 18th September",
        "Monday 21st September",
        "Friday 25th September",
        "Monday 28th September",
        "Friday 2nd October",
        "Monday 5th October",
        "Friday 9th October",
      ],
      bookingUrl: "/featured-tours/butler-saw",
      bookingPath: "/booking-journey?locationId=978&pageId=40c6aa85-2a28-49ee-b884-8839c71d3f4b"
    },
  ];

  return (
    <ToursContainer>
      <ParallaxBanner 
        title="Featured Tours"
        subtitle="Discover the Hidden Stories of Belvoir Castle"
        image={castleImage}
      />
      
      <ContentContainer>
        <SectionTitle>Guided Tours at Belvoir Castle</SectionTitle>
        <IntroText>
          Experience the rich history and fascinating stories of Belvoir Castle through our carefully curated guided tours. 
          Each tour offers a unique perspective on the castle's heritage, from its stunning art collection to its role in film and television.
        </IntroText>
        <IntroText style={{ fontSize: '1rem', marginBottom: '0', opacity: 0.75 }}>
          Tours can be added to your booking when purchasing tickets. Tours depart from the castle entrance.
        </IntroText>
        
        <ToursGrid>
          {tours.map((tour, index) => (
            <TourCardWrapper key={index}>
              <TourCard>
                <TourImage image={tour.image} />
                <TourContent>
                  <TourTitle>
                    <FontAwesomeIcon icon={tour.icon} />
                    {tour.title}
                  </TourTitle>
                  <TourDescription>{tour.description}</TourDescription>
                  <TourDetails>
                    {tour.duration && (
                      <div>
                        <FontAwesomeIcon icon={faClock} /> {tour.duration}
                      </div>
                    )}
                    {tour.price && (
                      <div>
                        <FontAwesomeIcon icon={faTicketAlt} /> {tour.price}
                      </div>
                    )}
                  </TourDetails>
                  {tour.dates && tour.dates.length > 0 && (
                    <TourDateTime>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <div>
                        <strong>Tour dates at {tour.time}:</strong>
                        <DateList>
                          {tour.dates.map((date, i) => (
                            <DateItem key={i}>{date}</DateItem>
                          ))}
                        </DateList>
                        {tour.moreDatesTBC && (
                          <MoreDatesBadge>More dates to be confirmed</MoreDatesBadge>
                        )}
                      </div>
                    </TourDateTime>
                  )}
                  <CardFooter>
                    {tour.bookingPath && (
                      <BookNowLink to={tour.bookingPath}>
                        Book now
                        <FontAwesomeIcon icon={faArrowRight} />
                      </BookNowLink>
                    )}
                  </CardFooter>
                </TourContent>
              </TourCard>
            </TourCardWrapper>
          ))}
        </ToursGrid>

      </ContentContainer>
      
      <Footer />
    </ToursContainer>
  );
};

export default FeaturedTours; 